import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Landmark category -> real stock photo URL mapping
const LANDMARK_IMAGES = {
  beach: 'https://images.unsplash.com/photo-1693221398153-437bea8475b9?w=600&q=80',
  harbor: 'https://images.unsplash.com/photo-1669143633171-2d2d2fbaa371?w=600&q=80',
  waterfront: 'https://images.unsplash.com/photo-1674072605500-7a0534b65a03?w=600&q=80',
  coastal: 'https://images.unsplash.com/photo-1762979790878-301b3d9962f8?w=600&q=80',
  historic_building: 'https://images.unsplash.com/photo-1649073448553-91e765aa1290?w=600&q=80',
  theater: 'https://images.unsplash.com/photo-1680896505838-b81d3d3951d3?w=600&q=80',
  town_center: 'https://images.unsplash.com/photo-1516458819443-0f257d640de9?w=600&q=80',
  colonial: 'https://images.unsplash.com/photo-1669087460556-2e44ebc02257?w=600&q=80',
  foliage: 'https://images.unsplash.com/photo-1761857112346-9c6404c2e505?w=600&q=80',
  forest_trail: 'https://images.unsplash.com/photo-1760985879385-be1a9f7f7f67?w=600&q=80',
  hills: 'https://images.unsplash.com/photo-1665890759638-0feb402f77f2?w=600&q=80',
  river: 'https://images.unsplash.com/photo-1698935366373-92d3105f28bf?w=600&q=80',
  castle: 'https://images.unsplash.com/photo-1666564424049-26cab5618df4?w=600&q=80',
  tower: 'https://images.unsplash.com/photo-1593741556926-10367e188890?w=600&q=80',
  stone_fort: 'https://images.unsplash.com/photo-1764011690902-d9cd4e701d1d?w=600&q=80',
};

// Map each city's landmarks to appropriate photo categories
const CITY_LANDMARK_PHOTOS = {
  'meriden-ct': [
    { img: 'town_center', label: 'Downtown Meriden' },
    { img: 'foliage', label: 'Hubbard Park & Castle Craig' },
    { img: 'hills', label: 'Hanging Hills' },
  ],
  'wallingford-ct': [
    { img: 'town_center', label: 'Historic Wallingford Center' },
    { img: 'colonial', label: 'Choate Rosemary Hall Campus' },
    { img: 'forest_trail', label: 'Tyler Mill Preserve' },
  ],
  'old-saybrook-ct': [
    { img: 'beach', label: "Harvey's Beach" },
    { img: 'historic_building', label: 'Katharine Hepburn Museum' },
    { img: 'harbor', label: 'Saybrook Point & Marina' },
  ],
  'madison-ct': [
    { img: 'beach', label: 'Hammonasset Beach State Park' },
    { img: 'town_center', label: 'Madison Town Center' },
    { img: 'colonial', label: 'Deacon John Grave House' },
  ],
  'guilford-ct': [
    { img: 'colonial', label: 'Guilford Town Green' },
    { img: 'historic_building', label: 'Henry Whitfield State Museum' },
    { img: 'beach', label: 'Jacobs Beach' },
  ],
  'branford-ct': [
    { img: 'harbor', label: 'Branford Town Green & Harbor' },
    { img: 'coastal', label: 'Thimble Islands' },
    { img: 'beach', label: 'Branford Point Beach' },
  ],
  'hamden-ct': [
    { img: 'forest_trail', label: 'Sleeping Giant State Park' },
    { img: 'town_center', label: 'Hamden Town Center' },
    { img: 'hills', label: 'Quinnipiac River Valley' },
  ],
  'middletown-ct': [
    { img: 'river', label: 'Connecticut River Waterfront' },
    { img: 'town_center', label: 'Historic Main Street' },
    { img: 'historic_building', label: 'Wesleyan University Campus' },
  ],
  'southington-ct': [
    { img: 'forest_trail', label: 'Mount Southington' },
    { img: 'town_center', label: 'Southington Town Center' },
    { img: 'foliage', label: 'Quinnipiac Trail' },
  ],
  'cheshire-ct': [
    { img: 'forest_trail', label: 'Cheshire Linear Trail' },
    { img: 'town_center', label: 'Cheshire Center' },
    { img: 'hills', label: 'Roaring Brook Falls' },
  ],
  'colchester-ct': [
    { img: 'colonial', label: 'Historic Colchester Village' },
    { img: 'foliage', label: 'Airline Trail' },
    { img: 'forest_trail', label: 'Day Pond State Park' },
  ],
  'deep-river-ct': [
    { img: 'river', label: 'Connecticut River Valley' },
    { img: 'town_center', label: 'Deep River Landing' },
    { img: 'historic_building', label: 'Historic Main Street' },
  ],
  'clinton-ct': [
    { img: 'harbor', label: 'Clinton Town Dock & Marina' },
    { img: 'beach', label: 'Clinton Town Beach' },
    { img: 'town_center', label: 'Clinton Crossing Premium Outlets' },
  ],
  'chester-ct': [
    { img: 'river', label: 'Chester-Hadlyme Ferry' },
    { img: 'town_center', label: 'Chester Village Shops' },
    { img: 'forest_trail', label: 'Cockaponset State Forest' },
  ],
  'westbrook-ct': [
    { img: 'beach', label: 'West Beach' },
    { img: 'harbor', label: 'Westbrook Town Dock' },
    { img: 'town_center', label: 'Westbrook Outlets' },
  ],
  'east-haddam-ct': [
    { img: 'theater', label: 'Goodspeed Opera House' },
    { img: 'castle', label: 'Gillette Castle State Park' },
    { img: 'forest_trail', label: "Devil's Hopyard State Park" },
  ],
  'middlefield-ct': [
    { img: 'foliage', label: 'Powder Ridge Mountain' },
    { img: 'forest_trail', label: 'Lyman Orchards' },
    { img: 'colonial', label: 'Historic Middlefield Center' },
  ],
};

// Services with their DB image filenames and slugs
const CITY_SERVICES = [
  { slug: 'kitchen-remodeling', name: 'Kitchen Remodeling', image: 'kitchen-remodel-marble-countertop-custom-cabinets-connecticut.jpg', desc: 'Transform your kitchen with custom cabinetry, granite countertops, modern appliances, and expert design.' },
  { slug: 'bathroom-remodeling', name: 'Bathroom Remodeling', image: 'modern-bathroom-marble-tile-walk-in-shower-connecticut.jpg', desc: 'Create a spa-like retreat with expert tile work, walk-in showers, modern vanities, and premium fixtures.' },
  { slug: 'deck-building', name: 'Trex Deck Building', image: 'composite-deck-white-railing-connecticut-home-remodeling.jpg', desc: 'Build your dream outdoor living space with low-maintenance Trex composite decking and custom railing.' },
  { slug: 'basement-finishing', name: 'Basement Finishing', image: 'basement-finishing-renovation-living-space-connecticut.jpg', desc: 'Maximize your living space with professional framing, drywall, flooring, and custom built-ins.' },
  { slug: 'vinyl-siding', name: 'Vinyl Siding', image: 'vinyl-siding-red-exterior-connecticut-home.jpg', desc: 'Protect and beautify your home with energy-efficient vinyl siding, trim, and soffit installation.' },
  { slug: 'window-replacement', name: 'Window Replacement', image: 'window-trim-installation-connecticut-contractor.jpg', desc: 'Upgrade to energy-efficient windows with professional installation, custom sizes, and expert trim work.' },
  { slug: 'custom-furniture', name: 'Custom Furniture', image: 'custom-wooden-cellar-hatch-connecticut-carpentry.jpg', desc: 'Handcrafted furniture and built-in cabinetry from quality hardwood, designed to your specifications.' },
  { slug: 'home-maintenance', name: 'Home Maintenance', image: 'general-home-maintenance-connecticut-handyman-services.jpg', desc: 'Comprehensive repair and maintenance including painting, drywall, door installation, and seasonal upkeep.' },
];

const CityPage = () => {
  const { cityId } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCity = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cities/${cityId}`);
        setCity(response.data);
      } catch (error) {
        console.error('Error fetching city:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCity();
  }, [cityId]);

  if (loading) return <div className="flex justify-center items-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3a87fe]"></div></div>;
  if (!city) return <div className="container mx-auto px-4 py-20 text-center"><h1 className="text-4xl font-bold mb-4">City Not Found</h1><Link to="/" className="text-[#3a87fe] hover:underline">Return Home</Link></div>;

  const landmarks = CITY_LANDMARK_PHOTOS[cityId] || CITY_LANDMARK_PHOTOS['meriden-ct'];

  const seoData = {
    title: `Home Remodeling ${city.name} CT | Kitchen & Bathroom Renovation ${city.name} Connecticut | Visser Construction`,
    description: city.meta_description || `Professional home remodeling contractor in ${city.name}, Connecticut. Kitchen remodeling, bathroom renovation, Trex decking, basement finishing near ${city.highlights?.[0] || city.name}. Licensed & insured. Free estimates.`,
    keywords: `home remodeling ${city.name} CT, ${city.name} kitchen remodeling, ${city.name} bathroom renovation, contractor ${city.name} Connecticut, ${city.name} home improvement`,
    canonical: `https://cthomeremodels.com/locations/${cityId}`,
    ogImage: `https://cthomeremodels.com/og/v2/city-${cityId}.png`,
    schemaData: {
      "@context": "https://schema.org",
      "@type": "GeneralContractor",
      "name": `Visser General Construction - ${city.name}, CT`,
      "url": `https://cthomeremodels.com/locations/${cityId}`,
      "telephone": "+1-203-604-3202",
      "address": { "@type": "PostalAddress", "streetAddress": "42 Gay St", "addressLocality": "Meriden", "addressRegion": "CT", "postalCode": "06450", "addressCountry": "US" },
      "areaServed": { "@type": "City", "name": city.name, "containedInPlace": { "@type": "State", "name": "Connecticut" } },
      "description": `Professional home remodeling services in ${city.name}, Connecticut. Kitchen, bathroom, decking, siding, windows, and more.`,
      "priceRange": "$$"
    }
  };

  return (
    <div className="city-page">
      <SEO {...seoData} />

      {/* Hero */}
      <section
        className="relative py-32 md:py-40 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('/api/media/name/driveway-repair-maintenance-connecticut-home-services.jpg')` }}
        data-testid="city-hero"
      >
        <div className="absolute top-6 left-6 z-10">
          <img src="/api/media/name/visser-general-construction-llc-logo-connecticut.png" alt={`Visser Construction - ${city.name} CT Home Remodeling`} className="h-20 md:h-28 object-contain drop-shadow-2xl" />
        </div>
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-[#5c0701] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {city.distance} from Meriden, CT
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
            Home Remodeling in <span className="text-[#3a87fe]">{city.name}, {city.state}</span>
          </h1>
          <p className="text-base md:text-lg text-white mb-8 drop-shadow-md max-w-3xl mx-auto">
            {city.tagline || city.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition" data-testid="city-cta-contact">
              Get a Free Quote
            </Link>
            <a href="tel:2036043202" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#5c0701] transition" data-testid="city-cta-call">
              Call (203) 604-3202
            </a>
          </div>
        </div>
      </section>

      {/* Local Landmarks with Real Photos */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-3 text-center">Proudly Serving {city.name}, Connecticut</h2>
          <p className="text-base text-gray-600 text-center mb-10 max-w-3xl mx-auto">
            {city.hero_content || city.history || `${city.name} is a vibrant Connecticut community`}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {landmarks.map((landmark, idx) => (
              <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-testid={`landmark-${idx}`}>
                <div className="h-48 overflow-hidden">
                  <img
                    src={LANDMARK_IMAGES[landmark.img]}
                    alt={`${landmark.label} - ${city.name}, Connecticut landmark`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 bg-gray-50">
                  <h3 className="font-bold text-[#5c0701]">{landmark.label}</h3>
                  <p className="text-sm text-gray-600">{city.name}, Connecticut</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expanded City Content - History, Neighborhoods, Local Considerations */}
      {city.history_section && (
        <section className="py-16 bg-gray-50" data-testid="city-history-section">
          <div className="container mx-auto px-4 max-w-4xl city-content" dangerouslySetInnerHTML={{ __html: city.history_section }} />
        </section>
      )}

      {city.neighborhoods_section && (
        <section className="py-16 bg-white" data-testid="city-neighborhoods-section">
          <div className="container mx-auto px-4 max-w-4xl city-content" dangerouslySetInnerHTML={{ __html: city.neighborhoods_section }} />
        </section>
      )}

      {/* Services with Real Project Photos */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold mb-3 text-center">Home Remodeling Services in {city.name}</h2>
          <p className="text-base text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Quality craftsmanship for {city.name} homeowners — from kitchens and bathrooms to decks and basements
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CITY_SERVICES.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}/${cityId}`}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex"
                data-testid={`city-service-${service.slug}`}
              >
                <div className="w-36 md:w-44 flex-shrink-0 overflow-hidden">
                  <img
                    src={`/api/media/name/${service.image}`}
                    alt={`${service.name} in ${city.name} CT by Visser Construction`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 flex flex-col justify-center">
                  <h3 className="text-lg font-bold text-[#5c0701] mb-2">{service.name} {city.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2">{service.desc}</p>
                  <span className="text-[#3a87fe] font-semibold text-sm">
                    View {city.name} {service.name} Details
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Local Considerations */}
      {city.local_considerations && (
        <section className="py-16 bg-white" data-testid="city-local-considerations">
          <div className="container mx-auto px-4 max-w-4xl city-content" dangerouslySetInnerHTML={{ __html: city.local_considerations }} />
        </section>
      )}

      {/* Why Choose Us - Expanded or Default */}
      {city.why_choose_us ? (
        <section className="py-16 bg-gray-50" data-testid="city-why-choose-expanded">
          <div className="container mx-auto px-4 max-w-4xl city-content" dangerouslySetInnerHTML={{ __html: city.why_choose_us }} />
        </section>
      ) : (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8">Why {city.name} Residents Choose Visser Construction</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div>
                <div className="bg-[#eaf2ff] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#3a87fe]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Quality Craftsmanship</h3>
                <p className="text-sm text-gray-600">Every {city.name} project completed with meticulous attention to detail and expert skill</p>
              </div>
              <div>
                <div className="bg-[#eaf2ff] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#3a87fe]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Local {city.name} Expertise</h3>
                <p className="text-sm text-gray-600">Deep knowledge of {city.name} neighborhoods, building codes, and architectural styles</p>
              </div>
              <div>
                <div className="bg-[#eaf2ff] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#3a87fe]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Licensed & Insured</h3>
                <p className="text-sm text-gray-600">Fully licensed and insured for all {city.name} residential construction and remodeling work</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#5c0701] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">Ready to Transform Your {city.name} Home?</h2>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Contact Visser General Construction today for a free home remodeling consultation in {city.name}, Connecticut
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition shadow-lg" data-testid="city-bottom-cta-contact">
              Get Your Free {city.name} Quote
            </Link>
            <a href="tel:2036043202" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#5c0701] transition" data-testid="city-bottom-cta-call">
              Call (203) 604-3202
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
